import React from 'react';
import { Link } from 'react-router-dom';
import CurrentlyReadingCard from './CurrentlyReadingCard';
import ReadingGoalCard from './ReadingGoalCard';
import ReadingStreakCard from './ReadingStreakCard';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../contexts/FirebaseAuthContext';

interface ReadingSchedule {
  date: string;
  time: string;
  title: string;
}

interface TimeSpentData {
  day: string;
  minutes: number;
}

const Dashboard: React.FC = () => {
  const { userPreferences } = useAuth();
  // Mock data for the time spent reading chart
  const timeSpentData: TimeSpentData[] = [
    { day: 'Mon', minutes: 255 },
    { day: 'Tue', minutes: 180 },
    { day: 'Wed', minutes: 300 },
    { day: 'Thu', minutes: 280 },
    { day: 'Fri', minutes: 320 },
    { day: 'Sat', minutes: 0 },
    { day: 'Sun', minutes: 0 },
  ];

  // Mock reading schedule
  const readingSchedule: ReadingSchedule[] = [
    { date: 'APR 05', time: 'Tomorrow at 7pm', title: 'Next Reading' },
    { date: 'APR 06', time: 'Sunday at 12 noon', title: 'Next Reading' },
    { date: 'APR 09', time: 'Wednesday 8 pm', title: 'Next Reading' },
  ];

  return (
    <div className="p-6 w-full px-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        {/* Replace with real user book data or leave blank if not available */}
        <div className="md:col-span-2">
          {/* Currently Reading Card */}
          <CurrentlyReadingCard
            coverUrl=""
            title=""
            author=""
            progress={0}
            currentPage={0}
            totalPages={0}
            startedOn=""
            onContinue={() => {}}
            onReadingList={() => {}}
            className="md:col-span-2"
          />
        </div>
        <div className="md:col-span-1">
          <ReadingGoalCard booksRead={4} goal={userPreferences?.goals?.booksPerYear || 12} />
        </div>
        <div className="md:col-span-1">
          <ReadingStreakCard streak={3} goal={7} daysLeft={4} />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* Time Spent Reading Section */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            {/* Header Row */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#FFF4E6]">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="9" cy="9" r="8" stroke="#FF9900" strokeWidth="1.5" />
                    <path d="M9 5V9L11.5 11" stroke="#FF9900" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="text-sm font-semibold text-gray-900">Time Spent Reading</span>
              </div>
              <button className="px-3 py-1 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50">This week</button>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={timeSpentData}>
                  <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 14, fill: '#888' }} />
                  <YAxis hide />
                  <Bar
                    dataKey="minutes"
                    fill="#FF6600"
                    radius={[6, 6, 0, 0]}
                    barSize={32}
                  />
                  <defs>
                    <linearGradient id="barOrange" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FF6600" stopOpacity={1} />
                      <stop offset="100%" stopColor="#FF9900" stopOpacity={0.8} />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* Stats Row */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-[#F8F9FA] rounded-xl px-6 py-4 text-center">
                <div className="text-xs text-gray-500 mb-1">Total Time</div>
                <div className="text-xl font-bold text-gray-900">255 mins</div>
              </div>
              <div className="bg-[#F8F9FA] rounded-xl px-6 py-4 text-center">
                <div className="text-xs text-gray-500 mb-1">Daily Average</div>
                <div className="text-xl font-bold text-gray-900">36 mins</div>
              </div>
              <div className="bg-[#F8F9FA] rounded-xl px-6 py-4 text-center">
                <div className="text-xs text-gray-500 mb-1">Best Day</div>
                <div className="text-xl font-bold text-gray-900">Sat</div>
              </div>
              <div className="bg-[#F8F9FA] rounded-xl px-6 py-4 text-center">
                <div className="text-xs text-gray-500 mb-1">Pages Read</div>
                <div className="text-xl font-bold text-gray-900">9842</div>
              </div>
            </div>
          </div>
        </div>
        {/* Right Column - Schedule only */}
        <div className="space-y-6">
          {/* Reading Schedule */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            {/* Header Row */}
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#E8F0FE]">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="5" width="12" height="9" rx="2" fill="#4285F4" fillOpacity="0.15" />
                  <rect x="3" y="5" width="12" height="9" rx="2" stroke="#4285F4" strokeWidth="1.5" />
                  <rect x="6" y="2" width="1.5" height="3" rx="0.75" fill="#4285F4" />
                  <rect x="10.5" y="2" width="1.5" height="3" rx="0.75" fill="#4285F4" />
                </svg>
              </span>
              <span className="text-sm font-semibold text-gray-900">Reading Schedule</span>
            </div>
            <div className="space-y-3 mb-4">
              {readingSchedule.map((schedule, index) => (
                <div key={index} className="flex items-center bg-white border border-gray-200 rounded-xl px-4 py-3">
                  <div className="w-16 text-center">
                    <p className="text-lg font-bold text-gray-900 leading-tight">{schedule.date}</p>
                  </div>
                  <div className="flex-1 ml-4">
                    <p className="text-xs text-gray-500 mb-0.5">Next Reading:</p>
                    <p className="text-base font-semibold text-gray-900 leading-tight">{schedule.time}</p>
                  </div>
                </div>
              ))}
            </div>
            <button className="w-full flex items-center justify-center gap-2 border border-gray-200 rounded-full py-3 text-base font-medium text-gray-700 hover:bg-gray-50 transition">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="#4285F4">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Schedule
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 